'use client';

import { cn } from '@lib/util/cn';
import { NAV_HEIGHT } from '@lib/util/nav-offset';
import type { MarkdownHeading } from '@lib/util/typography';
import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  baseHeadings: MarkdownHeading[];
  initialVariantHeadings?: MarkdownHeading[];
};

const SCROLL_OFFSET = NAV_HEIGHT.desktop + 32;

const ProductTableOfContents = ({
  baseHeadings,
  initialVariantHeadings = [],
}: Props) => {
  const [variantHeadings, setVariantHeadings] = useState<MarkdownHeading[]>(
    initialVariantHeadings,
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { headings } = (e as CustomEvent<{ headings: MarkdownHeading[] }>)
        .detail;
      setVariantHeadings(headings);
    };
    window.addEventListener('medusa:variantheadingschanged', handler);
    return () =>
      window.removeEventListener('medusa:variantheadingschanged', handler);
  }, []);

  const visibleHeadings = useMemo(() => {
    const seen = new Set<string>();
    return [...baseHeadings, ...variantHeadings].filter((h) => {
      if (h.level !== 2 && h.level !== 3) return false;
      if (seen.has(h.id)) return false;
      seen.add(h.id);
      return true;
    });
  }, [baseHeadings, variantHeadings]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const isManualScrolling = useRef(false);
  const rafId = useRef<number>(0);
  const olRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (visibleHeadings.length === 0) {
      return;
    }

    const updateActive = () => {
      if (isManualScrolling.current) {
        return;
      }
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        let current: string | null = null;
        for (const heading of visibleHeadings) {
          const el = document.getElementById(heading.id);
          if (el && el.getBoundingClientRect().top <= SCROLL_OFFSET) {
            current = heading.id;
          }
        }
        setActiveId(current);
      });
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateActive);
      cancelAnimationFrame(rafId.current);
    };
  }, [visibleHeadings]);

  useEffect(() => {
    const ol = olRef.current;
    if (!ol || !activeId) {
      return;
    }
    const activeEl = ol.querySelector(
      `[data-toc-id="${activeId}"]`,
    ) as HTMLElement | null;
    if (!activeEl) {
      return;
    }
    const olTop = ol.getBoundingClientRect().top;
    const olBottom = ol.getBoundingClientRect().bottom;
    const elTop = activeEl.getBoundingClientRect().top;
    const elBottom = activeEl.getBoundingClientRect().bottom;
    if (elTop < olTop) {
      ol.scrollBy({ top: elTop - olTop, behavior: 'smooth' });
    } else if (elBottom > olBottom) {
      ol.scrollBy({ top: elBottom - olBottom, behavior: 'smooth' });
    }
  }, [activeId]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) {
      return;
    }
    // Set active immediately — skip the scroll listener during animation
    setActiveId(id);
    isManualScrolling.current = true;
    const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
    // Re-enable natural scroll detection once animation settles (~600ms)
    setTimeout(() => {
      isManualScrolling.current = false;
    }, 600);
  };

  if (visibleHeadings.length === 0) {
    return null;
  }

  let h2Index = 0;

  return (
    <nav
      aria-label="Table of contents"
      className="flex flex-col mb-11 gap-3 w-full desktop:w-[310px] shrink-0 desktop:sticky desktop:top-44 desktop:self-start desktop:max-h-[calc(100vh-13rem)]"
    >
      <p className="font-heading text-lg leading-[1.125] tracking-[-0.01em] text-brand-neutral-900 shrink-0">
        Table of contents
      </p>
      <ol
        ref={olRef}
        className="flex flex-col gap-2 list-none text-sm leading-[1.5] overflow-y-auto min-h-0 [scrollbar-color:var(--brand-color-interactive-700)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-(--brand-color-interactive-700) [&::-webkit-scrollbar-track]:bg-transparent"
      >
        {visibleHeadings.map((heading) => {
          const isH3 = heading.level === 3;
          if (!isH3) {
            h2Index += 1;
          }
          return (
            <li
              key={`${heading.id}-${heading.level}`}
              data-toc-id={heading.id}
              className={isH3 ? 'pl-5' : ''}
            >
              <a
                href={`#${heading.id}`}
                onClick={(e) => handleClick(e, heading.id)}
                aria-current={activeId === heading.id ? 'true' : undefined}
                className={cn(
                  'transition-colors flex gap-2',
                  isH3 && 'text-[13px]',
                  {
                    'text-brand-interactive-700': activeId === heading.id,
                    'text-brand-neutral-600 hover:text-brand-neutral-800':
                      activeId !== heading.id && isH3,
                    'text-brand-neutral-700 hover:text-brand-neutral-900':
                      activeId !== heading.id && !isH3,
                  },
                )}
              >
                {!isH3 && (
                  <span className="shrink-0 tabular-nums">{h2Index}.</span>
                )}
                <span>{heading.text}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default ProductTableOfContents;
